/**
 * useFoundationState — Round 218A Phase 8D-3D (2026-02-07).
 *
 * Single hook for the platform's "foundation gate". Wraps
 * GET /api/foundation/state and exposes:
 *
 *   {
 *     loading, error,
 *     state,                    // full backend payload
 *     foundationComplete,       // bool
 *     missing,                  // string[]
 *     nextStep,                 // {key,label,route}|null
 *     businessBrainReady,
 *     brandBrainReady,
 *     offerReady,
 *     miReady,
 *     oiReady,
 *     refresh,                  // async () => void
 *   }
 *
 * Used by:
 *   * Money Bot / Content Bot / Growth Bot CTAs — to disable
 *     builder access until the foundation is complete.
 *   * Downstream builders — to auto-consume the Brains and skip
 *     any redundant "Who is your target customer?" question.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';

const EMPTY = {
  loading: true,
  error: null,
  state: null,
  foundationComplete: false,
  missing: [],
  nextStep: null,
  businessBrainReady: false,
  brandBrainReady: false,
  offerReady: false,
  miReady: false,
  oiReady: false,
};

export default function useFoundationState(active = true) {
  const [snap, setSnap] = useState(EMPTY);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/foundation/state');
      const s = res.data?.state || {};
      setSnap({
        loading: false,
        error: null,
        state: s,
        foundationComplete: !!s.foundation_complete,
        missing: Array.isArray(s.missing) ? s.missing : [],
        nextStep: s.next_step || null,
        businessBrainReady: !!s.business_brain_ready,
        brandBrainReady:    !!s.brand_brain_ready,
        offerReady:         !!s.offer_ready,
        miReady:            !!s.market_intelligence_ready,
        oiReady:            !!s.opportunity_intelligence_ready,
      });
    } catch (err) {
      setSnap({ ...EMPTY, loading: false, error: err });
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    load();
  }, [active, load]);

  return { ...snap, refresh: load };
}
